import {
    ApproveStatus,
    BesuApproveStatus,
    ClaimStateWithNestedStatus,
    ClaimStatus,
    ClaimStatusWithNestedStatus,
} from "../types/claim";
import { UserRole } from "../types/userCurrent";

import { compareDateDesc } from "./common";
import { hasNestedInvoiceAction } from "./invoice";
import { isUserClaimApprover, isUserContractor, isUserPO } from "./role";

/**
 * !! Please use useHasClaimAction hook if you can provide full claimState, or you need to check different approvers state
 *
 * Without currentApproverStatus, PARTIAL_CA_APPROVED will always consider as false (no action).
 */
export const hasClaimAction = (
    roles: UserRole[],
    claimStatus: ClaimStatus,
    isAtLeastOneInvoiceCreated: boolean,
    currentApproverStatus?: BesuApproveStatus,
): boolean => {
    switch (claimStatus) {
        case ClaimStatus.SUBMITTED:
        case ClaimStatus.RESUBMITTED:
        case ClaimStatus.PARTIAL_CA_APPROVED:
            if (isUserClaimApprover(roles)) {
                return currentApproverStatus === ApproveStatus.PENDING;
            }

            return false;

        case ClaimStatus.ALL_CA_APPROVED:
            return isUserPO(roles);

        case ClaimStatus.APPROVED:
            return isUserContractor(roles) && !isAtLeastOneInvoiceCreated;
        case ClaimStatus.CA_REJECTED:
        case ClaimStatus.PO_REJECTED:
            return isUserContractor(roles);
        default:
            return false;
    }
};

export const hasNestedClaimAction = (
    roles: UserRole[],
    nestedClaimStatus: ClaimStatusWithNestedStatus,
    approverStatus?: ApproveStatus,
    currentUserEmail?: string,
): boolean => {
    const isAtLeastOneInvoiceCreated =
        nestedClaimStatus.invoiceStatuses.length > 0;
    return (
        hasClaimAction(
            roles,
            nestedClaimStatus.claimStatus,
            isAtLeastOneInvoiceCreated,
            approverStatus,
        ) ||
        nestedClaimStatus.invoiceStatuses.some((nestedInvoiceStatus) =>
            hasNestedInvoiceAction(
                roles,
                nestedInvoiceStatus,
                currentUserEmail,
            ),
        )
    );
};

export const getNestedClaimStatus = (
    nestedClaimState: ClaimStateWithNestedStatus,
): ClaimStatusWithNestedStatus => ({
    claimStatus: nestedClaimState.claimState.status,
    invoiceStatuses: nestedClaimState.invoiceStatuses,
});

const compareNestedClaimActionFirst =
    (roles: UserRole[], currentUserEmail?: string) =>
    (
        a: ClaimStatusWithNestedStatus,
        b: ClaimStatusWithNestedStatus,
    ): number => {
        if (
            hasNestedClaimAction(roles, a, undefined, currentUserEmail) &&
            hasNestedClaimAction(roles, b, undefined, currentUserEmail)
        ) {
            return 0;
        } else if (
            hasNestedClaimAction(roles, a, undefined, currentUserEmail)
        ) {
            return -1;
        } else if (
            hasNestedClaimAction(roles, b, undefined, currentUserEmail)
        ) {
            return 1;
        } else {
            return 0;
        }
    };

export const compareClaimDateDescAndActionFirst =
    (roles: UserRole[], currentUserEmail?: string) =>
    (a: ClaimStateWithNestedStatus, b: ClaimStateWithNestedStatus): number => {
        const compareActionResult = compareNestedClaimActionFirst(
            roles,
            currentUserEmail,
        )(getNestedClaimStatus(a), getNestedClaimStatus(b));
        if (compareActionResult === 0) {
            return compareDateDesc(a.claimState, b.claimState);
        } else {
            return compareActionResult;
        }
    };
